import { log } from '../services/production-logger';

/**
 * MCP iOS Simulator Test Helpers
 * Utilities for ensuring components are properly configured for MCP testing
 */

import { Platform, ViewStyle } from 'react-native';

/**
 * Generates MCP-compatible test props for components
 */
export const getMCPTestProps = (
  componentName: string,
  identifier?: string | number,
  accessibilityLabel?: string
) => {
  const testId = identifier 
    ? `${componentName}-${identifier}`.toLowerCase().replace(/\s+/g, '-')
    : componentName.toLowerCase().replace(/\s+/g, '-');

  return {
    testID: testId,
    accessibilityLabel: accessibilityLabel || componentName,
    accessible: true,
  };
};

/**
 * Generates MCP-compatible props for buttons and touchable components
 */
export const getMCPButtonProps = (
  action: string,
  identifier?: string,
  hint?: string
) => {
  const label = identifier ? `${action} ${identifier}` : action;
  
  return {
    ...getMCPTestProps(action, identifier, label),
    accessibilityRole: 'button' as const,
    accessibilityHint: hint,
  };
};

/**
 * Generates MCP-compatible props for input fields
 */
export const getMCPInputProps = (
  fieldName: string,
  placeholder?: string
) => {
  return {
    ...getMCPTestProps(`input-${fieldName}`, undefined, fieldName),
    accessibilityRole: 'text' as const,
    accessibilityHint: placeholder || `Enter ${fieldName}`,
  };
};

/**
 * Generates MCP-compatible props for list items
 */
export const getMCPListItemProps = (
  itemType: string,
  itemId: string | number,
  itemLabel?: string
) => {
  return {
    ...getMCPTestProps(`${itemType}-item`, itemId, itemLabel),
    accessibilityRole: 'button' as const,
  };
};

/**
 * Generates MCP-compatible props for navigation components
 */
export const getMCPNavigationProps = (
  screenName: string,
  actionType: 'tab' | 'back' | 'navigate' = 'navigate'
) => {
  const action = actionType === 'tab' ? 'tab' : actionType === 'back' ? 'back-button' : 'navigate';
  
  return {
    ...getMCPTestProps(`${action}-${screenName}`, undefined, `${actionType} to ${screenName}`),
    accessibilityRole: 'button' as const,
  };
};

/**
 * Generates MCP-compatible props for modal components
 */
export const getMCPModalProps = (
  modalName: string,
  isBackdrop = false
) => {
  const componentType = isBackdrop ? 'backdrop' : 'modal';
  
  return {
    ...getMCPTestProps(`${modalName}-${componentType}`),
    accessibilityViewIsModal: !isBackdrop,
    ...(isBackdrop && { accessibilityRole: 'button' as const }),
  };
};

/**
 * Ensures touch targets meet iOS HIG minimum size (44x44)
 */
export const getMCPTouchTargetStyle = (style?: ViewStyle): ViewStyle => {
  return {
    minHeight: 44,
    minWidth: 44,
    ...style,
  };
};

/**
 * Helper to add delays for animations and transitions
 */
export const mcpWaitForAnimation = (duration = 300): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, duration));
};

/**
 * Helper to log MCP test identifiers for debugging
 */
export const logMCPIdentifiers = (componentName: string, identifiers: Record<string, string>) => {
  if (__DEV__) {
    log.debug(`[MCP Test IDs] ${componentName}:`, identifiers);
  }
};

/**
 * Common test IDs for frequently used components
 */
export const MCPTestIds = {
  // Navigation
  TAB_HOME: 'tab-home',
  TAB_PURCHASES: 'tab-purchases',
  TAB_CAPTURE: 'tab-capture',
  TAB_INVENTORY: 'tab-inventory',
  TAB_VENDORS: 'tab-vendors',
  
  // Common actions
  BUTTON_SAVE: 'button-save',
  BUTTON_CANCEL: 'button-cancel',
  BUTTON_DELETE: 'button-delete',
  BUTTON_EDIT: 'button-edit',
  BUTTON_ADD: 'button-add',
  BUTTON_SEARCH: 'button-search',
  BUTTON_FILTER: 'button-filter',
  BUTTON_SORT: 'button-sort',
  
  // Auth
  INPUT_EMAIL: 'input-email',
  INPUT_PASSWORD: 'input-password',
  BUTTON_LOGIN: 'button-login',
  BUTTON_LOGOUT: 'button-logout',
  BUTTON_REGISTER: 'button-register',
  
  // Modals
  MODAL_CONFIRM: 'modal-confirm',
  MODAL_ALERT: 'modal-alert',
  MODAL_PICKER: 'modal-picker',
  
  // Lists
  LIST_PURCHASES: 'list-purchases',
  LIST_VENDORS: 'list-vendors',
  LIST_INVENTORY: 'list-inventory',
} as const;

export default {
  getMCPTestProps,
  getMCPButtonProps,
  getMCPInputProps,
  getMCPListItemProps,
  getMCPNavigationProps,
  getMCPModalProps,
  getMCPTouchTargetStyle,
  mcpWaitForAnimation,
  logMCPIdentifiers,
  MCPTestIds,
};
